import { useEffect, useState } from 'react';
import { AvgTimePerDistruptions, isEmptyString } from '../constants/defaults';
import {
  DEVELOPER_BENEFIT,
  DEVELOPER_HRLY_RATE,
  DEVELOPER_YRLY_RATE,
  ITOPS_BENEFIT,
  ITOPS_YRLY_RATE,
  RS_FIFTY_THOUSAND,
  RS_TEN_THOUSAND,
  RS_THIRTY_THOUSAND,
  RS_TWO_HUNDRED_THOUSAND,
} from '../constants/prices';
import { useCurrency } from 'src/common/hooks/widgets/useCurrency';

export interface PriceProps {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: [
    {
      qty: number;
      price: number;
    },
  ];
}

export interface EfficiencyGainsProps {
  developers: number;
  preventions: number;
  speedGain: number;
  avgTimePerDistruptions: AvgTimePerDistruptions;
  itOpsEmployees: number;
  openSourceTechnoList: string;
  currency: string;
  userLocale: string;
}

export interface RevenueGainProps {
  preventions: number;
  speedGain: number;
  avgTimePerDistruptions: AvgTimePerDistruptions;
  totalEffiencyGains: string;
}

/* hook to generate the revenue gains */
export const setRevenueSavings = ({
  preventions,
  speedGain,
  avgTimePerDistruptions,
  totalEffiencyGains,
}: RevenueGainProps) => {
  const [hourlyRate10000, setHourlyRate10000] = useState(0);
  const [hourlyRate30000, setHourlyRate30000] = useState(0);
  const [hourlyRate50000, setHourlyRate50000] = useState(0);
  const [hourlyRate200000, setHourlyRate200000] = useState(0);

  const calculateRevenueSaving = (hourlyRate: number) => {
    const developer =
      avgTimePerDistruptions.reduction * hourlyRate * preventions;
    const itOpsDeveloper =
      avgTimePerDistruptions.speedGain * hourlyRate * speedGain;
    return Math.round(developer + itOpsDeveloper);
  };

  useEffect(() => {
    setHourlyRate10000(calculateRevenueSaving(RS_TEN_THOUSAND));
    setHourlyRate30000(calculateRevenueSaving(RS_THIRTY_THOUSAND));
    setHourlyRate50000(calculateRevenueSaving(RS_FIFTY_THOUSAND));
    setHourlyRate200000(calculateRevenueSaving(RS_TWO_HUNDRED_THOUSAND));
  }, [preventions, speedGain, totalEffiencyGains]);

  return [hourlyRate10000, hourlyRate30000, hourlyRate50000, hourlyRate200000];
};

/* hook to generate the effiecency gains */
export const setEffiencyGains = ({
  developers,
  preventions,
  speedGain,
  avgTimePerDistruptions,
  itOpsEmployees,
  openSourceTechnoList,
  currency,
  userLocale,
}: EfficiencyGainsProps) => {
  const [developerEffiencyGains, setDeveloperEffiencyGains] = useCurrency(
    currency,
    userLocale,
    0,
  );
  const [itOpsEffiencyGainsRawValue, setItOpsEffiencyGainsRawValue] =
    useState(0);
  const [devEffiencyGainsRawValue, setDevEffiencyGainsRawValue] = useState(0);
  const [openSourceRawValue, setOpenSourceGainsRawValue] = useState(0);
  const [itOpsEffiencyGains, setItOpsEffiencyGains] = useCurrency(
    currency,
    userLocale,
    0,
  );
  const [openSourceGains, setOpenSourceGains] = useCurrency(
    currency,
    userLocale,
    0,
  );
  const [totalEffiencyGains, setTotalEffiencyGains] = useCurrency(
    currency,
    userLocale,
    0,
  );

  const calculateDeveloperEffeciencyGains = () => {
    const outageReductionValue =
      developers *
      DEVELOPER_HRLY_RATE *
      avgTimePerDistruptions.reduction *
      preventions;

    const speedGainValue =
      developers *
      DEVELOPER_HRLY_RATE *
      avgTimePerDistruptions.speedGain *
      speedGain;

    const calculateDevGains = Math.round(outageReductionValue + speedGainValue);
    setDevEffiencyGainsRawValue(calculateDevGains);
    setDeveloperEffiencyGains(calculateDevGains);
  };

  const calculateItOpsEffeciencyGains = () => {
    const outageReductionValue =
      itOpsEmployees *
      DEVELOPER_HRLY_RATE *
      avgTimePerDistruptions.reduction *
      preventions;

    const speedGainValue =
      itOpsEmployees *
      DEVELOPER_HRLY_RATE *
      avgTimePerDistruptions.speedGain *
      speedGain;

    const calculateOpsGains = Math.round(outageReductionValue + speedGainValue);
    setItOpsEffiencyGainsRawValue(calculateOpsGains);
    setItOpsEffiencyGains(calculateOpsGains);
  };

  const calculateOpenSourceGains = () => {
    if (!isEmptyString(openSourceTechnoList)) {
      const developerGains =
        DEVELOPER_BENEFIT * developers * DEVELOPER_YRLY_RATE;
      const itOpsGains = ITOPS_BENEFIT * itOpsEmployees * ITOPS_YRLY_RATE;
      setOpenSourceGainsRawValue(developerGains + itOpsGains);
      setOpenSourceGains(developerGains + itOpsGains);
    } else {
      setOpenSourceGainsRawValue(0);
      setOpenSourceGains(0);
    }
  };

  const calculateTotalEffiencyGains = () => {
    setTotalEffiencyGains(
      Math.round(
        itOpsEffiencyGainsRawValue +
          devEffiencyGainsRawValue +
          openSourceRawValue,
      ),
    );
  };

  useEffect(() => {
    calculateDeveloperEffeciencyGains();
    calculateItOpsEffeciencyGains();
    calculateOpenSourceGains();
    calculateTotalEffiencyGains();
  }, [
    developers,
    preventions,
    speedGain,
    itOpsEmployees,
    itOpsEffiencyGainsRawValue,
    devEffiencyGainsRawValue,
    openSourceRawValue,
    openSourceTechnoList,
    avgTimePerDistruptions,
  ]);

  return [
    developerEffiencyGains,
    itOpsEffiencyGains,
    openSourceGains,
    totalEffiencyGains,
  ];
};
